import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOffline } from '../context/OfflineContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Download,
  Wifi,
  WifiOff,
  Trash2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Cloud,
  CloudOff,
  HardDrive,
  Loader2
} from 'lucide-react';

export default function OfflineSettings() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const {
    isOnline,
    offlineData,
    isDownloading,
    downloadProgress,
    lastSyncTime,
    pendingChanges,
    downloadOfflineContent,
    syncPendingChanges,
    clearOfflineData,
    hasOfflineData
  } = useOffline();
  
  const [selectedLevel, setSelectedLevel] = useState(null);

  const handleDownload = async () => {
    const result = await downloadOfflineContent(selectedLevel);
    if (result.success) {
      toast.success(`Downloaded ${result.stats.flashcards_count} flashcards, ${result.stats.stories_count} stories, ${result.stats.lessons_count} lessons!`);
    } else {
      toast.error(`Download failed: ${result.error}`);
    }
  };

  const handleSync = async () => {
    const result = await syncPendingChanges();
    if (result.success) {
      toast.success('Progress synced successfully!');
    } else {
      toast.error('Sync failed. Will retry when online.');
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all offline data?')) {
      clearOfflineData();
      toast.success('Offline data cleared');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="offline-settings-page">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-white hover:bg-white/10"
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            Back
          </Button>
          
          <h1 className="text-xl font-bold text-white">Offline Mode</h1>
          
          <Badge className={isOnline ? 'bg-emerald-500' : 'bg-amber-500'}>
            {isOnline ? (
              <><Wifi className="h-4 w-4 mr-1" /> Online</>
            ) : (
              <><WifiOff className="h-4 w-4 mr-1" /> Offline</>
            )}
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Status Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {hasOfflineData ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-400" />
              )}
              Offline Status
            </CardTitle>
            <CardDescription className="text-slate-400">
              {hasOfflineData 
                ? 'Content is available for offline use'
                : 'No offline content downloaded yet'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasOfflineData && offlineData && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-900/50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-400">
                    {offlineData.stats?.lessons_count || 0}
                  </p>
                  <p className="text-sm text-slate-400">Lessons</p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-400">
                    {offlineData.stats?.flashcards_count || 0}
                  </p>
                  <p className="text-sm text-slate-400">Flashcards</p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-400">
                    {offlineData.stats?.stories_count || 0}
                  </p>
                  <p className="text-sm text-slate-400">Stories</p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-400">
                    {offlineData.stats?.exercises_count || 0}
                  </p>
                  <p className="text-sm text-slate-400">Exercises</p>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between text-sm text-slate-400">
              <span>Last synced: {formatDate(lastSyncTime)}</span>
              {pendingChanges.length > 0 && (
                <Badge variant="outline" className="text-amber-400 border-amber-400">
                  {pendingChanges.length} pending changes
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Download Section */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-400" />
              Download Content
            </CardTitle>
            <CardDescription className="text-slate-400">
              Download lessons, flashcards, and stories for offline use
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Level Selection */}
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Select level (optional):</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedLevel === null ? 'default' : 'outline'}
                  onClick={() => setSelectedLevel(null)}
                  className={selectedLevel === null ? 'bg-emerald-500' : 'text-white border-slate-600'}
                  size="sm"
                >
                  All Levels
                </Button>
                {['A1', 'A2', 'B1', 'B2'].map((level) => (
                  <Button
                    key={level}
                    variant={selectedLevel === level ? 'default' : 'outline'}
                    onClick={() => setSelectedLevel(level)}
                    className={selectedLevel === level ? 'bg-emerald-500' : 'text-white border-slate-600'}
                    size="sm"
                  >
                    {level}
                  </Button>
                ))}
              </div>
            </div>

            {/* Download Progress */}
            {isDownloading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Downloading...</span>
                  <span className="text-emerald-400">{downloadProgress}%</span>
                </div>
                <Progress value={downloadProgress} className="h-2 bg-slate-700" />
              </div>
            )}

            {/* Download Button */}
            <Button 
              onClick={handleDownload}
              disabled={isDownloading || !isOnline}
              className="w-full bg-emerald-500 hover:bg-emerald-600"
            >
              {isDownloading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Downloading...</>
              ) : (
                <><Cloud className="h-4 w-4 mr-2" /> Download {selectedLevel || 'All'} Content</>
              )}
            </Button>

            {!isOnline && (
              <p className="text-sm text-amber-400 text-center">
                <WifiOff className="h-4 w-4 inline mr-1" />
                You need to be online to download content
              </p>
            )}
          </CardContent>
        </Card>

        {/* Sync Section */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-purple-400" />
              Sync Progress
            </CardTitle>
            <CardDescription className="text-slate-400">
              Sync your offline progress back to the cloud
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingChanges.length > 0 ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-amber-400 text-sm">
                  You have {pendingChanges.length} unsaved changes that need to be synced.
                </p>
              </div>
            ) : (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <p className="text-emerald-400 text-sm">
                  All your progress is synced!
                </p>
              </div>
            )}

            <Button 
              onClick={handleSync}
              disabled={pendingChanges.length === 0 || !isOnline}
              variant="outline"
              className="w-full text-white border-slate-600 hover:bg-slate-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Now
            </Button>
          </CardContent>
        </Card>

        {/* Storage Management */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-slate-400" />
              Storage Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleClear}
              variant="outline"
              className="w-full text-red-400 border-red-400/50 hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All Offline Data
            </Button>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-lg">💡 Tips for Offline Learning</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-slate-400 text-sm">
            <p>• Download content before going offline (e.g., before a flight or commute)</p>
            <p>• Your progress will be saved locally and synced when you&apos;re back online</p>
            <p>• Flashcards and stories work best offline - AI chat requires internet</p>
            <p>• Download specific levels to save storage space</p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
